// Universal product helper functions for all product types
// 
// IMPORTANT: Product Name Usage Guidelines
// =======================================
// 
// 1. RAW 'name' FIELD - Internal Use Only:
//    - Data processing & lookups
//    - Search functionality
//    - Logging & debugging  
//    - Analytics & tracking
//    - Alt text & accessibility
//    - Error handling
//
// 2. Use product.name directly - Already includes brand prefixes:
//    - All visible text in UI
//    - Page titles & headings  
//    - Product cards & listings
//    - Dropdowns & selections
//
// This ensures consistent branding with brand prefixes already included in
// the data, creating a single source of truth for all product names.

import { batteriesData } from '../data/batteriesData';
import { screensData } from '../data/screensData';
import { transducersData } from '../data/transducersData';
import { shuttlesData } from '../data/shuttlesData';
import { polesData } from '../data/polesData';
import { bundlesData } from '../data/bundlesData';

/**
 * Convert product name to valid JavaScript component name
 * @param {string} productName - The display name of the product
 * @returns {string} - Valid component name starting with "Simple"
 */
export const nameToComponentName = (productName) => {
  return 'Simple' + productName
    .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special chars like +, -, etc.
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
};

/**
 * Convert product name to URL-friendly path
 * @param {string} productName - The display name of the product
 * @returns {string} - URL-friendly path
 */
export const nameToUrlPath = (productName) => {
  return productName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove special chars
    .trim()
    .replace(/\s+/g, '-'); // Replace spaces with hyphens
};

/**
 * Find a product by ID across all product types
 * @param {string} productId - The product ID to search for
 * @returns {Object|null} - Product object with productType added, or null if not found
 */
export const findProductById = (productId) => {
  // Check batteries (including variants)
  for (const battery of batteriesData) {
    if (battery.id === productId) {
      return { ...battery, productType: 'battery' };
    }
    // Check variants if they exist
    if (battery.variants) {
      if (battery.variants.withCharger?.id === productId) {
        return { 
          ...battery.variants.withCharger, 
          productType: 'battery', 
          parentId: battery.id,
          // Inherit image from parent if not specified in variant
          image: battery.variants.withCharger.image || battery.image,
          fallbackImage: battery.variants.withCharger.fallbackImage || battery.fallbackImage
        };
      }
      if (battery.variants.withoutCharger?.id === productId) {
        return { 
          ...battery.variants.withoutCharger, 
          productType: 'battery', 
          parentId: battery.id,
          // Inherit image from parent if not specified in variant
          image: battery.variants.withoutCharger.image || battery.image,
          fallbackImage: battery.variants.withoutCharger.fallbackImage || battery.fallbackImage
        };
      }
    }
  }

  // Check screens
  for (const screen of screensData) {
    if (screen.id === productId) {
      return { ...screen, productType: 'screen' };
    }
  }

  // Check transducers
  for (const transducer of transducersData) {
    if (transducer.id === productId) {
      return { ...transducer, productType: 'transducer' };
    }
  }

  // Check shuttles
  for (const shuttle of shuttlesData) {
    if (shuttle.id === productId) {
      return { ...shuttle, productType: 'shuttle' };
    }
  }

  // Check poles
  for (const pole of polesData) {
    if (pole.id === productId) {
      return { ...pole, productType: 'pole' };
    }
  }

  // Check bundles
  for (const bundle of bundlesData) {
    if (bundle.id === productId) {
      return { ...bundle, productType: 'bundle' };
    }
  }

  return null;
};

/**
 * Get the image URL for a product, with fallback handling
 * @param {Object} product - The product object
 * @returns {string} - Image URL or fallback
 */
export const getProductImage = (product) => {
  if (product.image) {
    return product.image;
  }
  
  if (product.fallbackImage) {
    return product.fallbackImage;
  }

  // Generate a placeholder image
  const productName = encodeURIComponent(product.name || 'Product');
  return `https://dummyimage.com/400x200/cccccc/000000&text=${productName}`;
};



/**
 * Get the MSRP for a product
 * @param {Object} product - The product object
 * @returns {number|null} - MSRP or null if not available
 */
export const getProductMSRP = (product) => {
  return product.msrp || null;
};

/**
 * Get the CSV lookup ID for a product
 * @param {Object} product - The product object
 * @returns {string|null} - CSV lookup ID or null if not available
 */
export const getProductCsvLookupId = (product) => {
  return product.csvLookupId || null;
};

/**
 * Get the display name for a product
 * @param {Object} product - The product object
 * @returns {string} - Display name
 */
export const getProductDisplayName = (product) => {
  return product.name || 'Unknown Product';
};

/**
 * Examples of generated component names (using product.name with brand prefixes):
 */
// nameToComponentName("Garmin ECHOMAP UHD2 73sv") → "SimpleGarminECHOMAPUHD273sv"
// nameToComponentName("Garmin LiveScope Plus LVS34") → "SimpleGarminLiveScopePlusLVS34"
// nameToComponentName("Amped Outdoors 8Ah LiFePO4 + 3A Charger") → "SimpleAmpedOutdoors8AhLiFePO4Plus3ACharger"
// nameToComponentName("Garmin GT56UHD") → "SimpleGarminGT56UHD" 